/* eslint-disable class-methods-use-this */
import { Injectable } from "@angular/core";
import posthog from "posthog-js";
import { AnalyticsTrackEvent } from "../models/analytics/analytics.model";

/**
 * @category Service
 */
@Injectable({
  providedIn: "root",
})
export class AnalyticsService {
  private _isInitialized = false;
  _userID: string;
  _userInfo: Record<string, any>;

  initialize(config: { apiKey?: string; apiHost?: string }) {
    if (this._isInitialized || !config?.apiKey) {
      return;
    }

    try {
      posthog.init(config.apiKey, {
        api_host: config.apiHost || "https://us.i.posthog.com",
        capture_pageview: true,
        capture_pageleave: true,
        capture_exceptions: {
          capture_unhandled_errors: true,
          capture_unhandled_rejections: true,
          // Keep console.error capture off to avoid noisy exception volumes.
          capture_console_errors: false,
        },
        person_profiles: "identified_only",
      });
      this._isInitialized = true;
    } catch {
      // PostHog unavailable or misconfigured — app must still bootstrap.
    }
  }

  /**
   * Once called, subsequent events tracked will be associated with the user
   * identified here.
   */
  identifyUser(userID: string, userInfo: Record<string, any> = {}) {
    this._userID = userID;
    this._userInfo = userInfo;
    this._safePosthog(() => posthog.identify(userID, userInfo));
  }

  /**
   * Records an event.
   *
   * Calls PostHog capture API to record a user-triggered event.
   */
  record<T>(event: AnalyticsTrackEvent<T>) {
    this._safePosthog(() =>
      posthog.capture(event.type, this._appendUserIDToEvent(event.detail))
    );
  }

  /**
   * Sends a caught error to PostHog error tracking (complements automatic
   * uncaught exception / unhandled rejection capture configured in init).
   * Use for errors handled in try/catch or app-level error handlers.
   */
  captureException(error: unknown, context?: Record<string, unknown>) {
    this._safePosthog(() => posthog.captureException(error, context));
  }

  /** Runs PostHog SDK calls so failures never break user flows or error handling. */
  private _safePosthog(fn: () => void): void {
    if (!this._isInitialized) {
      return;
    }
    try {
      fn();
    } catch {
      // Intentionally swallow: analytics must not affect the app.
    }
  }

  /**
   * Append user-identifiable information to the event, if possible.
   *
   * Adds user identity/context onto tracked properties for easier filtering.
   *
   * @param eventDetail of type `T` in `AnalyticsTrackEvent<T>`
   */
  _appendUserIDToEvent<T>(eventDetail: T): (T & { category: string }) | T {
    if (this._userID || this._userInfo) {
      // Historical property name: `category` carries the user id for filtering in analytics tools.
      return { ...eventDetail, category: this._userID, ...this._userInfo };
    }
    return eventDetail;
  }
}
